import { FormikProvider, useFormik } from "formik";
import { useCallback, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootStateInterface } from "reducer";
import * as Yup from "yup";

import features from "features";
import { LayerAlias, ddRegex } from "constant";
import LightButton from "components/buttons/LightButton/LightButton";
import PrimaryButton from "components/buttons/PrimaryButton/PrimaryButton";
import ModalWrapper from "components/modals/ModalWrapper/ModalWrapper";
import ClassificationForm from "components/modalForms/ClassificationForm/ClassificationForm";
import MapManualPointModalForm from "scenes/MapPage/components/MapManualPointModalForm/MapManualPointModalForm";

import styles from "./classification-point-modal.module.scss";

const validationSchema = Yup.object().shape({
  generalPointData: Yup.object().shape({
    name: Yup.string().required("Name is required"),
    coordinates: Yup.object({
      lat: Yup.string().matches(ddRegex, "Invalid latitude format").required(),
      lng: Yup.string().matches(ddRegex, "Invalid longitude format").required()
    })
  })
});

interface ClassificationPointModalInterface {
  title?: string;
  description?: string;
  classificationsData: any;
  currentClassificationData: any;
  generalPointData: any;
  pointsLayerAlias: LayerAlias;
  onSubmit;
}

const ClassificationPointModal = () => {
  const dispatch = useDispatch();

  const {
    title,
    description,
    classificationsData,
    onSubmit,
    currentClassificationData,
    generalPointData,
    pointsLayerAlias
  } = useSelector<RootStateInterface, ClassificationPointModalInterface>(
    (state) => state.modal.modalProps
  );

  const [currentType, setCurrentType] = useState({
    label: "Unclassified",
    value: "unclassified"
  });

  const onModalClose = useCallback(() => {
    dispatch(features.unitsManager.actions.unfreezeHistory());
    dispatch(features.modal.actions.hideModal());
  }, [dispatch]);

  const onDecline = useCallback(() => {
    dispatch(features.modal.actions.hideModal());
  }, [dispatch]);

  const formFields = useMemo(() => {
    return classificationsData.items.find(
      (item) => item.value === currentType.value
    );
  }, [currentType.value, classificationsData.items]);

  const onSubmitFormik = useCallback(
    (values) => {
      const classificationValues = values.classification;
      const generalPointValues = {
        ...values.generalPointData,
        coordinates: {
          lat: Number(values.generalPointData.coordinates.lat),
          lng: Number(values.generalPointData.coordinates.lng)
        }
      };

      const classification = {
        alias: currentType.value,
        name: currentType.label,
        color: formFields?.color,
        fields: formFields?.form?.map(
          ({ alias: fieldAlias, name, type, items }) => {
            return {
              alias: fieldAlias,
              value:
                type === "text" || type === "integer"
                  ? classificationValues[fieldAlias]
                  : items.find(
                      (item) => item.value === classificationValues[fieldAlias]
                    )?.name,
              value_alias: classificationValues[fieldAlias],
              name,
              type
            };
          }
        )
      };

      const parsedData = {
        ...generalPointValues,
        classification
      };

      onSubmit(parsedData);
    },
    [
      onSubmit,
      currentType.label,
      currentType.value,
      formFields?.color,
      formFields?.form
    ]
  );

  const formik = useFormik({
    validateOnChange: true,
    initialValues: {
      generalPointData: { ...generalPointData },
      classification:
        currentClassificationData?.fields?.reduce((acc, item) => {
          acc[item.alias] = item.value_alias;
          return acc;
        }, {}) ?? {}
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    onSubmit: onSubmitFormik
  });

  return (
    <ModalWrapper className={styles["modal-container"]} onHide={onModalClose}>
      <div className={styles["modal"]}>
        <div className={styles["modal__text-container"]}>
          <h2 className={styles["modal__title"]}>
            {title ??
              (pointsLayerAlias !== LayerAlias.MANUAL
                ? "Classifications"
                : "Point")}
          </h2>
          {(description || pointsLayerAlias !== LayerAlias.MANUAL) && (
            <h4 className={styles["modal__description"]}>
              {description ?? "Add a classification to the found object."}
            </h4>
          )}
        </div>
        <FormikProvider value={formik}>
          {pointsLayerAlias !== LayerAlias.MANUAL ? (
            <ClassificationForm
              formik={formik}
              classificationsData={classificationsData}
              currentClassificationData={currentClassificationData}
              currentType={currentType}
              setCurrentType={setCurrentType}
              formFields={formFields}
            />
          ) : (
            <MapManualPointModalForm
              formik={formik}
              classificationsData={classificationsData}
              currentClassificationData={currentClassificationData}
              currentType={currentType}
              setCurrentType={setCurrentType}
              formFields={formFields}
              isLock={false}
            />
          )}
          <div className={styles["modal__controls-wrapper"]}>
            <LightButton textSize={14} onClick={onDecline}>
              Cancel
            </LightButton>
            <PrimaryButton textSize={14} onClick={formik.submitForm}>
              Save
            </PrimaryButton>
          </div>
        </FormikProvider>
      </div>
    </ModalWrapper>
  );
};
export default ClassificationPointModal;
